import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Save } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import {
  SegmentedToggle,
  type SegmentedOption,
} from "../../components/SegmentedToggle/SegmentedToggle";
import { useToast } from "../../components/Toast/Toast";

import { ApiError, describeError } from "../../lib/errors";
import { updateGuild } from "./api";
import type { GuildDetail, GuildVisibility, UpdateGuildInput } from "./types";
import styles from "./GuildSettingsForm.module.css";

// === Visibility options ============================================== //
// Mirror the segmented control of docs/mockups/guild-detail.html — Open /
// Invite only.
const VISIBILITY_OPTIONS: SegmentedOption<GuildVisibility>[] = [
  { value: "open", label: "Open" },
  { value: "invite_only", label: "Invite only" },
];

function visibilityLabel(v: GuildVisibility): string {
  return v === "open" ? "Open" : "Invite only";
}

// === Component ====================================================== //
// A child of GuildDetailPage (Task 13). It owns the Settings card and the
// sticky save bar; the parent owns the shared query and re-syncs it from the
// `onSaved` callback. The dirty baseline is the `guild` prop — the parent
// remounts this component (keyed on the saved record) after a save so the
// useState initializers re-read the freshly-saved values.

interface GuildSettingsFormProps {
  projectId: string;
  guildId: string;
  /** The loaded guild — the form's baseline for dirty tracking. */
  guild: GuildDetail;
  /** Called with the updated guild after a successful PATCH. */
  onSaved: (updated: GuildDetail) => void;
}

export function GuildSettingsForm({
  projectId,
  guildId,
  guild,
  onSaved,
}: GuildSettingsFormProps) {
  const { push } = useToast();

  // --- Settings field state ------------------------------------------ //
  const [name, setName] = useState(guild.name);
  const [description, setDescription] = useState(guild.description);
  const [visibility, setVisibility] = useState<GuildVisibility>(
    guild.visibility,
  );

  // --- Per-field validation ------------------------------------------ //
  const trimmedName = name.trim();
  const nameError =
    trimmedName.length < 1 || trimmedName.length > 64
      ? "Guild name must be between 1 and 64 characters."
      : undefined;
  // Description is sent raw (untrimmed) — validate the raw length to match.
  const descError =
    description.length > 512
      ? "Description must be 512 characters or fewer."
      : undefined;

  // --- Dirty tracking ------------------------------------------------ //
  const nameDirty = name !== guild.name;
  const descDirty = description !== guild.description;
  const visDirty = visibility !== guild.visibility;

  const dirty = nameDirty || descDirty || visDirty;
  const changeCount =
    (nameDirty ? 1 : 0) + (descDirty ? 1 : 0) + (visDirty ? 1 : 0);

  const errorCount = (nameError ? 1 : 0) + (descError ? 1 : 0);
  const hasError = errorCount > 0;

  // === Save ========================================================== //
  const mutation = useMutation({
    mutationFn: (body: UpdateGuildInput) =>
      updateGuild(projectId, guildId, body),
  });

  // ApiError save failure → a generic alert above the card (the
  // PlayerDetailForm serverGenericError pattern).
  const serverError = mutation.error instanceof ApiError ? mutation.error : null;
  const serverGenericError = serverError
    ? describeError(serverError.code)
    : undefined;

  function doSave() {
    if (hasError || !dirty) return;
    // Only the dirty fields are sent. `description` is sent as the raw current
    // value (untrimmed) — parity with the domain.
    const body: UpdateGuildInput = {};
    if (nameDirty) body.name = trimmedName;
    if (descDirty) body.description = description;
    if (visDirty) body.visibility = visibility;
    mutation.mutate(body, {
      onSuccess: (updated) => {
        push({ variant: "success", message: "Guild updated" });
        onSaved(updated);
      },
    });
  }

  function doDiscard() {
    setName(guild.name);
    setDescription(guild.description);
    setVisibility(guild.visibility);
    mutation.reset();
  }

  return (
    <>
      {serverGenericError && (
        <div className={styles.errorAlert}>
          <Alert variant="danger" title="Couldn't save changes">
            {serverGenericError}
          </Alert>
        </div>
      )}

      {/* ───────── Settings ───────── */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>Settings</h3>
          <span className={styles.cardSub}>
            name · description · visibility
          </span>
        </div>
        <div className={styles.cardBody}>
          {/* Name */}
          <div className={`${styles.inputGroup} ${styles.fieldGap}`}>
            <label htmlFor="guild-name">
              Name
              {nameDirty && (
                <span className={styles.modDot} title="modified" aria-hidden />
              )}
            </label>
            <input
              id="guild-name"
              className={`${styles.input} ${nameError ? styles.invalid : ""} ${
                !nameError && nameDirty ? styles.modified : ""
              }`}
              value={name}
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? "guild-name-err" : undefined}
              onChange={(e) => setName(e.target.value)}
            />
            {nameError && (
              <div className={styles.fieldError} id="guild-name-err">
                {nameError}
              </div>
            )}
          </div>

          {/* Description */}
          <div className={`${styles.inputGroup} ${styles.fieldGap}`}>
            <label htmlFor="guild-description">
              Description
              {descDirty && (
                <span className={styles.modDot} title="modified" aria-hidden />
              )}
            </label>
            <textarea
              id="guild-description"
              className={`${styles.textarea} ${
                descError ? styles.invalid : ""
              } ${!descError && descDirty ? styles.modified : ""}`}
              spellCheck={false}
              value={description}
              aria-invalid={descError ? true : undefined}
              aria-describedby={descError ? "guild-description-err" : undefined}
              onChange={(e) => setDescription(e.target.value)}
            />
            {descError && (
              <div className={styles.fieldError} id="guild-description-err">
                {descError}
              </div>
            )}
          </div>

          {/* Visibility */}
          <div className={styles.inputGroup}>
            <label id="guild-visibility-label">
              Visibility
              {visDirty && (
                <span className={styles.modDot} title="modified" aria-hidden />
              )}
            </label>
            <SegmentedToggle
              options={VISIBILITY_OPTIONS}
              value={visibility}
              onChange={setVisibility}
              aria-label="Visibility"
            />
            {visDirty ? (
              <div className={styles.helper}>
                Was{" "}
                <code className={styles.code}>
                  {visibilityLabel(guild.visibility)}
                </code>
                .
              </div>
            ) : (
              <div className={styles.helper}>
                Open — anyone can join. Invite only — players need an invite.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ───────── Sticky save bar ───────── */}
      {dirty && (
        <div
          className={styles.saveBar}
          role="region"
          aria-label="Unsaved changes"
        >
          <div className={styles.inner}>
            <div
              className={`${styles.status} ${hasError ? styles.statusErr : ""}`}
            >
              <span className={styles.statusDot} aria-hidden />
              {hasError
                ? `Fix ${errorCount} ${
                    errorCount === 1 ? "error" : "errors"
                  } to save`
                : `${changeCount} unsaved ${
                    changeCount === 1 ? "change" : "changes"
                  }`}
            </div>
            <div className={styles.saveActions}>
              <Button
                variant="ghost"
                onClick={doDiscard}
                disabled={mutation.isPending}
              >
                Discard
              </Button>
              <Button
                variant="primary"
                onClick={doSave}
                disabled={hasError || !dirty || mutation.isPending}
                loading={mutation.isPending}
                icon={
                  !mutation.isPending ? (
                    <Save size={13} aria-hidden />
                  ) : undefined
                }
              >
                {mutation.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
